import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './ResumeUpload.css';

const ResumeUpload = ({ onUpload, onParse, processing }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  // Allowed file types
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  
  // Max file size in bytes (5MB)
  const maxSize = 5 * 1024 * 1024;
  
  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };
  
  // Validate and set file
  const validateAndSetFile = (selectedFile) => {
    // Reset error
    setError('');
    
    // Check if file exists
    if (!selectedFile) {
      return;
    }
    
    // Check file type
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Invalid file type. Please upload a PDF, DOC, DOCX, or TXT file.');
      return;
    }
    
    // Check file size
    if (selectedFile.size > maxSize) {
      setError('File size exceeds 5MB. Please upload a smaller file.');
      return;
    }
    
    // Set file
    setFile(selectedFile);
    
    // Call onUpload callback
    if (onUpload) {
      onUpload(selectedFile);
    }
  };
  
  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    
    // Get dropped file
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };
  
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  
  const handleDragLeave = () => {
    setDragging(false);
  };
  
  // Handle browse click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };
  
  // Handle parse click
  const handleParseClick = () => {
    if (file && onParse) {
      onParse(file);
    }
  };
  
  // Get file name and extension
  const getFileInfo = () => {
    if (!file) return { name: '', extension: '' };
    
    const name = file.name;
    const lastDot = name.lastIndexOf('.');
    const extension = lastDot !== -1 ? name.substring(lastDot + 1).toUpperCase() : '';
    
    return { name, extension };
  };
  
  const { name, extension } = getFileInfo();
  
  return (
    <div className="resume-upload">
      <div 
        className={`upload-area ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt"
          className="file-input"
        />
        
        {!file ? (
          // Upload instructions
          <div className="upload-instructions">
            <div className="upload-icon">
              <i className="fas fa-file-upload"></i>
            </div>
            <h3 className="upload-title">Upload Your Resume</h3>
            <p className="upload-text">
              Drag and drop your resume file here, or
              <button 
                type="button" 
                className="browse-button" 
                onClick={handleBrowseClick}
              >
                browse
              </button>
              to select a file
            </p>
            <p className="upload-hint">
              Supported formats: PDF, DOC, DOCX, TXT (Max 5MB)
            </p>
          </div>
        ) : (
          // File preview
          <div className="file-preview">
            <div className={`file-icon file-${extension.toLowerCase()}`}>
              <span className="file-extension">{extension}</span>
            </div>
            <div className="file-info">
              <p className="file-name">{name}</p>
              <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
            </div>
            <button 
              type="button" 
              className="remove-file-button"
              onClick={() => setFile(null)}
              disabled={processing}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
      
      {error && (
        <div className="upload-error">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      )}
      
      {file && (
        <div className="upload-actions">
          <button
            type="button"
            className="btn btn-primary parse-button"
            onClick={handleParseClick}
            disabled={processing}
          >
            {processing ? (
              <>
                <span className="spinner-small"></span>
                Processing...
              </>
            ) : (
              'Parse Resume'
            )}
          </button>
          <p className="upload-help">
            We'll extract information from your resume automatically to speed up the profile creation process.
          </p>
        </div>
      )}
    </div>
  );
};

ResumeUpload.propTypes = {
  onUpload: PropTypes.func,
  onParse: PropTypes.func,
  processing: PropTypes.bool
};

ResumeUpload.defaultProps = {
  onUpload: () => {},
  onParse: () => {},
  processing: false
};

export default ResumeUpload;